// src/services/institutionService.js
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';

export class InstitutionService {
  // Get current user ID
  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  // Check if user is authenticated
  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  // Create institution
  static async createInstitution(institutionData) {
    const userId = this.ensureAuthenticated();

    try {
      // Check if institution already exists
      const existingInstitution = await this.findExistingInstitution(institutionData);
      
      if (existingInstitution) {
        // Add user to existing institution
        await this.addMemberToInstitution(existingInstitution.id, userId);
        return existingInstitution.id;
      }

      // Create new institution
      const docRef = await addDoc(collection(db, 'taskdata-institutions'), {
        ...institutionData,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating institution:', error);
      throw new Error('Failed to create institution');
    }
  }

  // Find existing institution
  static async findExistingInstitution(institutionData) {
    try {
      let q;
      
      if (institutionData.type === 'School') {
        q = query(
          collection(db, 'taskdata-institutions'),
          where('type', '==', 'School'),
          where('schoolName', '==', institutionData.schoolName),
          where('district', '==', institutionData.district),
          where('province', '==', institutionData.province)
        );
      } else {
        q = query(
          collection(db, 'taskdata-institutions'),
          where('type', '==', institutionData.type),
          where('district', '==', institutionData.district),
          where('province', '==', institutionData.province)
        );
      }

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error finding existing institution:', error);
      return null;
    }
  }

  // Add member to institution
  static async addMemberToInstitution(institutionId, userId) {
    try {
      const institutionRef = doc(db, 'taskdata-institutions', institutionId);
      await updateDoc(institutionRef, {
        members: arrayUnion(userId),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error adding member to institution:', error);
      throw new Error('Failed to add member to institution');
    }
  }

  // Get user's institutions
  static async getUserInstitutions() {
    const userId = this.ensureAuthenticated();

    try {
      const q = query(
        collection(db, 'taskdata-institutions'),
        where('members', 'array-contains', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching user institutions:', error);
      throw new Error('Failed to fetch institutions');
    }
  }

  // Get institution details
  static async getInstitution(institutionId) {
    this.ensureAuthenticated();

    try {
      const docRef = doc(db, 'taskdata-institutions', institutionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      } else {
        throw new Error('Institution not found');
      }
    } catch (error) {
      console.error('Error fetching institution:', error);
      throw error;
    }
  }

  // Update institution
  static async updateInstitution(institutionId, updates) {
    const userId = this.ensureAuthenticated();

    try {
      const institutionRef = doc(db, 'taskdata-institutions', institutionId);
      
      // Verify user has permission to update
      const institution = await this.getInstitution(institutionId);
      if (institution.createdBy !== userId && !institution.members?.includes(userId)) {
        throw new Error('Permission denied: You cannot update this institution');
      }

      await updateDoc(institutionRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating institution:', error);
      throw error;
    }
  }

  // Remove member from institution
  static async removeMemberFromInstitution(institutionId, memberUserId) {
    const userId = this.ensureAuthenticated();

    try {
      const institution = await this.getInstitution(institutionId);
      
      // Only creator or the member themselves can remove membership
      if (institution.createdBy !== userId && memberUserId !== userId) {
        throw new Error('Permission denied');
      }

      const institutionRef = doc(db, 'taskdata-institutions', institutionId);
      await updateDoc(institutionRef, {
        members: arrayRemove(memberUserId),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error removing member from institution:', error);
      throw error;
    }
  }

  // Search institutions
  static async searchInstitutions(searchTerm, filters = {}) {
    const userId = this.ensureAuthenticated();

    try {
      let q = collection(db, 'taskdata-institutions');
      
      // Apply filters
      if (filters.type) {
        q = query(q, where('type', '==', filters.type));
      }
      if (filters.province) {
        q = query(q, where('province', '==', filters.province));
      }
      if (filters.district) {
        q = query(q, where('district', '==', filters.district));
      }

      const querySnapshot = await getDocs(q);
      let institutions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter by search term if provided
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        institutions = institutions.filter(institution => 
          institution.name?.toLowerCase().includes(term) ||
          institution.schoolName?.toLowerCase().includes(term)
        );
      }

      return institutions;
    } catch (error) {
      console.error('Error searching institutions:', error);
      throw new Error('Failed to search institutions');
    }
  }
}